import React, { useMemo, useState } from 'react';
import Dialog from '@shared/ui/molecules/Dialog';
import { closeDialog } from '@shared/store/dialog.duck';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { BodyLarge } from '@shared/ui/atoms/fonts/Body';
import { useDispatch, useSelector } from "react-redux";
import { GroupedApprovalRequest } from '@features/holidays/models/approval-requests.models';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import { WhereaboutsHeaderImage } from "../../components/WhereaboutsHeaderImage";
import { findWhereaboutsOptionById, iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { FreeBusyStatus } from '@features/whereabouts/services/WhereaboutOptions';
import { selectIconPack } from '@shared/store/auth.duck';
import moment from "moment";
import { PRETTY_DATE_FORMAT } from '@shared/utils/DateUtils';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { selectApprovals, setApprovals } from '@features/holidays/store/approvalRequests.duck';
import { cancelApproval } from '@features/holidays/services/ApprovalRequests';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function CancelApprovedLeaveDialog(props: Props) {
  const allWhereaboutsOptions = useSelector(selectAllWhereaboutsOptions);
  const iconPack = useSelector(selectIconPack);
  const {approval} = props.payload;
  const dispatch = useDispatch();
  const approvalsList = useSelector(selectApprovals);
  const [loading, setLoading] = useState(false);

  const amWhereabouts = useMemo(() => {
    if (approval.amWhereaboutsId === undefined) return undefined;
    return findWhereaboutsOptionById(allWhereaboutsOptions, approval.amWhereaboutsId);
  }, [allWhereaboutsOptions, approval]);

  const pmWhereabouts = useMemo(() => {
    if (approval.pmWhereaboutsId === undefined) return undefined;
    return findWhereaboutsOptionById(allWhereaboutsOptions, approval.pmWhereaboutsId);
  }, [allWhereaboutsOptions, approval]);

  const whereaboutsImageUrl = useMemo(() => {
    const image = amWhereabouts?.image ?? pmWhereabouts?.image ?? undefined;
    if (image) {
      return iconPackPath(iconPack, image, FreeBusyStatus.FREE);
    }
  }, [amWhereabouts?.image, iconPack, pmWhereabouts?.image]);

  const whereaboutsLabel = useMemo(() => {
    return amWhereabouts?.label ?? pmWhereabouts?.label ?? 'Error!';
  }, [amWhereabouts?.label, pmWhereabouts?.label]);

  const confirmCancel = async () => {
    setLoading(true);
    try {
      await cancelApproval(approval);
      const filteredApprovalsList = approvalsList.filter(a => a !== approval);
      dispatch(setApprovals(filteredApprovalsList));
      dispatch(closeDialog());
    } finally {
      setLoading(false);
    }
  }

  const close = () => {
    dispatch(closeDialog());
  }

  return (
    <DialogWrapper>
      <Dialog isOpen={true} onClose={() => dispatch(closeDialog())}>
        <DialogContent>
          {loading && <LoadingSpinner hideText={true} hideBorder={false} fullScreen={true} />}
          {whereaboutsImageUrl && <WhereaboutsHeaderImage imageUrl={whereaboutsImageUrl} />}

          <DialogHeader title="Cancel Approved Leave?"/>

          <BodyLarge style={{marginTop: 16, marginBottom: 0}} weight={600}>{whereaboutsLabel}</BodyLarge>
          <BodyLarge style={{marginBottom: 32}} weight={400} colour={Colours.darkGrey}>{moment(approval.fromDate).format(PRETTY_DATE_FORMAT)}</BodyLarge>

          <OutlineButton className={'cancelApproval__yes'}
                         click={confirmCancel}
                         disabled={loading}
                         style={{marginBottom: 12}}
                         text="Yes, Cancel Leave"
                         fullWidth={false}
                         size={ButtonSize.Large}/>
          <OutlineButton className={'cancelApproval__no'}
                         disabled={loading}
                         click={close}
                         text="No, Don't Cancel Leave"
                         fullWidth={false}
                         size={ButtonSize.Large}/>
        </DialogContent>
      </Dialog>
    </DialogWrapper>
  )
}

interface Props {
  payload: Payload;
}

interface Payload {
  approval: GroupedApprovalRequest;
}

const DialogWrapper = styled.div`
  .dialog__content {
    width: 412px;
  }
`

const DialogContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 16px;
  .cancelApproval__yes, .cancelApproval__yes p {
    border-color: ${Colours.red};
    color: ${Colours.red};
  }
  .cancelApproval__no, .cancelApproval__no p {
    border-color: ${Colours.darkGrey};
    color: ${Colours.darkGrey};
  }
`
